import type { ComponentProps, ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Separator as SeparatorPrimitive, VisuallyHidden as VH } from "radix-ui";
import { cn } from "@rekati/utils";

export function Card({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="card"
      className={cn("rounded-lg border bg-card text-card-foreground", className)}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: ComponentProps<"div">) {
  return <div data-slot="card-header" className={cn("grid gap-1 p-5 pb-3", className)} {...props} />;
}

export function CardTitle({ className, children, ...props }: ComponentProps<"h3">) {
  return (
    <h3 data-slot="card-title" className={cn("text-base font-semibold tracking-tight", className)} {...props}>
      {children}
    </h3>
  );
}

export function CardDescription({ className, ...props }: ComponentProps<"p">) {
  return (
    <p data-slot="card-description" className={cn("text-sm text-muted-foreground", className)} {...props} />
  );
}

export function CardContent({ className, ...props }: ComponentProps<"div">) {
  return <div data-slot="card-content" className={cn("p-5 pt-0", className)} {...props} />;
}

export const badgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-xs border px-2 py-0.5 font-mono text-[0.72rem] font-medium tracking-wide uppercase whitespace-nowrap",
  {
    variants: {
      tone: {
        neutral: "border-border bg-secondary text-secondary-foreground",
        volt: "border-transparent bg-volt text-volt-foreground",
        success: "border-success/30 bg-success/10 text-success",
        warning: "border-warning/30 bg-warning/10 text-warning",
        danger: "border-destructive/30 bg-destructive/10 text-destructive",
        outline: "border-border bg-transparent text-muted-foreground",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export function Badge({
  className,
  tone,
  ...props
}: ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  return <span data-slot="badge" className={cn(badgeVariants({ tone }), className)} {...props} />;
}

/** A keyboard key, for the "press Enter" hints of the question flow. */
export function Kbd({ className, ...props }: ComponentProps<"kbd">) {
  return (
    <kbd
      className={cn(
        "inline-flex h-5 min-w-5 items-center justify-center rounded-xs border border-b-2 px-1 font-mono text-[0.7rem] font-medium text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

export function Spinner({ className, label = "Loading" }: { className?: string; label?: string }) {
  return (
    <span role="status" className={cn("inline-flex items-center", className)}>
      <span
        className="size-5 animate-spin rounded-full border-2 border-current border-r-transparent"
        aria-hidden="true"
      />
      <VisuallyHidden>{label}</VisuallyHidden>
    </span>
  );
}

export function Skeleton({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="skeleton"
      aria-hidden="true"
      className={cn("animate-pulse rounded-md bg-muted", className)}
      {...props}
    />
  );
}

export function Separator({
  className,
  orientation = "horizontal",
  ...props
}: ComponentProps<typeof SeparatorPrimitive.Root>) {
  return (
    <SeparatorPrimitive.Root
      data-slot="separator"
      orientation={orientation}
      className={cn(
        "shrink-0 bg-border data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px",
        className,
      )}
      {...props}
    />
  );
}

export function VisuallyHidden({ children }: { children: ReactNode }) {
  return <VH.Root>{children}</VH.Root>;
}

/** Live region for status lines that change without focus moving (WCAG 4.1.3). */
export function LiveRegion({ children, assertive }: { children: ReactNode; assertive?: boolean }) {
  return (
    <div aria-live={assertive ? "assertive" : "polite"} aria-atomic="true" className="sr-only">
      {children}
    </div>
  );
}

export function Alert({
  tone = "neutral",
  title,
  children,
  icon,
  className,
  action,
}: {
  tone?: "neutral" | "danger" | "success" | "warning";
  title?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  className?: string;
  action?: ReactNode;
}) {
  const tones = {
    neutral: "border-border bg-card",
    danger: "border-destructive/40 bg-destructive/8",
    success: "border-success/40 bg-success/8",
    warning: "border-warning/40 bg-warning/8",
  } as const;
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      className={cn("flex gap-3 rounded-md border p-4 text-sm", tones[tone], className)}
    >
      {icon ? (
        <span className="mt-0.5 shrink-0 [&_svg]:size-5" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <div className="grid flex-1 gap-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className="text-muted-foreground">{children}</div> : null}
      </div>
      {action ? <div className="shrink-0 self-center">{action}</div> : null}
    </div>
  );
}
